/**
 * Support and policy pages. These exist because the footer previously pointed
 * "Çatdırılma" at /checkout, "Qaytarma" at /account and "Əlaqə" at a route that
 * was never defined — dead ends at exactly the moment a shopper looks for
 * reassurance.
 *
 * Delivery and alterations copy is read from src/content/policies.js so it can
 * never drift from what the cart and checkout actually promise.
 */
import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import SeoMeta from "../components/storefront/SeoMeta";
import { useI18n } from "../lib/i18n";
import { pickLocale } from "../lib/format";
import { useCatalogSettings } from "../hooks/useCatalogSettings";
import { DELIVERY, NEEDS_CONFIRMATION, policyText } from "../content/policies";

function PolicyPage({ eyebrow, title, intro, children, testid }) {
  return (
    <div className="bg-[var(--adf-cream)] text-[var(--adf-ink)]" data-testid={testid}>
      <SeoMeta title={title} description={intro} />
      <Header />
      <main id="main" className="adf-container py-16 lg:py-24">
        <span className="adf-small text-[var(--adf-gray-3)]">{eyebrow}</span>
        <h1 className="mt-4 font-serif text-4xl italic lg:text-5xl">{title}</h1>
        {intro && <p className="mt-6 max-w-2xl text-[15px] leading-8 text-[var(--adf-gray-2)]">{intro}</p>}
        <div className="mt-12 max-w-2xl space-y-10">{children}</div>
        <ConciergeFallback />
      </main>
      <Footer />
    </div>
  );
}

function Section({ heading, children }) {
  return (
    <section>
      <h2 className="font-serif text-2xl">{heading}</h2>
      <div className="mt-4 space-y-3 text-[15px] leading-8 text-[var(--adf-gray-2)]">{children}</div>
    </section>
  );
}

/** Shown on every policy page so a shopper always has a way to reach a human. */
function ConciergeFallback() {
  return (
    <div className="mt-16 border border-[var(--adf-gray-4)] bg-white/50 p-8">
      <h2 className="font-serif text-xl">Sualınız var?</h2>
      <p className="mt-3 text-[15px] leading-8 text-[var(--adf-gray-2)]">
        Concierge komandamız ölçü, çatdırılma və düzəliş sualları üzrə kömək edir.
      </p>
      <Link to="/contact" className="mt-5 inline-block adf-cta adf-cta-dark px-6 py-3">
        Concierge ilə əlaqə
      </Link>
    </div>
  );
}

/**
 * The admin writes these as free text with blank lines between paragraphs —
 * the returns policy on production is seven of them, including a list of
 * non-returnable goods. Rendering it as one blob would bury the terms.
 */
function Paragraphs({ text }) {
  return String(text || "")
    .split(/\n\s*\n/)
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => (
      <p key={part.slice(0, 40)} className="whitespace-pre-line">{part}</p>
    ));
}

export function DeliveryPage() {
  const { locale } = useI18n();
  const settings = useCatalogSettings();
  /* The admin panel owns this copy. The page used to state "free on every
     order, no minimum" while the admin value said "free over 500 AZN" and the
     product page printed the admin one — three answers to the same question. */
  const adminCopy = pickLocale(settings?.delivery_info, locale);
  return (
    <PolicyPage
      testid="page-delivery"
      eyebrow="Dəstək"
      title="Çatdırılma"
      intro={adminCopy || policyText(DELIVERY.ribbon, locale)}
    >
      <Section heading="Çatdırılma üsulları">
        <ul className="list-disc space-y-2 pl-5">
          {DELIVERY.methods.map((m) => (
            <li key={m.code}>{policyText(m, locale)}</li>
          ))}
        </ul>
      </Section>
      {adminCopy && (
        <Section heading="Çatdırılma şərtləri">
          <Paragraphs text={adminCopy} />
        </Section>
      )}
    </PolicyPage>
  );
}

export function ReturnsPage() {
  const { locale } = useI18n();
  const settings = useCatalogSettings();
  const adminCopy = pickLocale(settings?.return_info, locale);
  const days = NEEDS_CONFIRMATION.returnWindowDays;
  return (
    <PolicyPage
      testid="page-returns"
      eyebrow="Dəstək"
      title="Qaytarma"
      intro="Aldığınız parça gözlədiyiniz kimi olmadıqda bizimlə əlaqə saxlayın."
    >
      <Section heading="Qaytarma şərtləri">
        {adminCopy ? (
          <Paragraphs text={adminCopy} />
        ) : days ? (
          <p>Sifarişi təhvil aldığınız gündən {days} gün ərzində qaytara bilərsiniz.</p>
        ) : (
          <>
            <p>
              Qaytarma müddəti və şərtləri concierge komandası tərəfindən fərdi olaraq
              təsdiqlənir. Qaytarma tələbi üçün bizimlə əlaqə saxlayın.
            </p>
            <p>Parça geyinilməmiş və orijinal vəziyyətdə olmalıdır.</p>
          </>
        )}
      </Section>
      <Section heading="Sifarişlərim">
        <p>
          Sifarişlərinizi və çatdırılma statusunu{" "}
          <Link to="/account" className="underline underline-offset-4">hesabınızda</Link> izləyə bilərsiniz.
        </p>
      </Section>
    </PolicyPage>
  );
}

export function ContactPage() {
  const { contactEmail, contactPhone, atelierAddress } = NEEDS_CONFIRMATION;
  const hasDirectChannel = contactEmail || contactPhone || atelierAddress;
  return (
    <PolicyPage
      testid="page-contact"
      eyebrow="Dəstək"
      title="Əlaqə"
      intro="Atelier Di Falco komandası ilə əlaqə saxlayın."
    >
      {hasDirectChannel ? (
        <Section heading="Birbaşa əlaqə">
          {contactPhone && <p>Telefon: {contactPhone}</p>}
          {contactEmail && <p>E-poçt: {contactEmail}</p>}
          {atelierAddress && <p>Ünvan: {atelierAddress}</p>}
        </Section>
      ) : (
        <Section heading="Bizə yazın">
          <p>
            Concierge formu vasitəsilə bizə yazın — iş saatlarında ən qısa müddətdə
            cavab veririk.
          </p>
        </Section>
      )}
    </PolicyPage>
  );
}

export function PrivacyPage() {
  return (
    <PolicyPage
      testid="page-privacy"
      eyebrow="Hüquqi"
      title="Məxfilik siyasəti"
      intro="Şəxsi məlumatlarınızı necə toplayır və istifadə edirik."
    >
      <Section heading="Topladığımız məlumatlar">
        <p>
          Hesab yaratdıqda ad, e-poçt və telefon nömrənizi; sifariş verdikdə isə
          çatdırılma ünvanınızı saxlayırıq.
        </p>
      </Section>
      <Section heading="İstifadə məqsədi">
        <p>
          Məlumatlar yalnız sifarişin icrası, çatdırılma və müştəri dəstəyi üçün
          istifadə olunur.
        </p>
      </Section>
      <Section heading="Hüquqlarınız">
        <p>
          Məlumatlarınıza baxmaq, düzəliş etmək və ya silinməsini tələb etmək üçün
          bizimlə əlaqə saxlayın.
        </p>
      </Section>
    </PolicyPage>
  );
}

export function TermsPage() {
  return (
    <PolicyPage
      testid="page-terms"
      eyebrow="Hüquqi"
      title="İstifadə şərtləri"
      intro="Saytdan istifadə qaydaları."
    >
      <Section heading="Sifarişlər">
        <p>
          Sifariş təsdiqləndikdən sonra stok mövcudluğu yoxlanılır; parça mövcud
          deyilsə sizinlə əlaqə saxlanılır.
        </p>
      </Section>
      <Section heading="Qiymətlər">
        <p>Bütün qiymətlər AZN ilə göstərilir.</p>
      </Section>
    </PolicyPage>
  );
}
